import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";
import { getConfig } from "@jti/fe-common";
import { CasPurposesApi, OmpApi, SnpApi } from "@jti/rest-api";
import Mail from "@jti/ui/icons/mail.svg";
import { useUser } from "../../../../hooks/useUser";
import { Button, CheckboxInput, DEPRECATED_TextInput, Form, FormField, Loading, Recaptchabar, replacePlaceholdersWithLinks, ResponsiveImage, useForm, validationPatterns, } from "../../../../index";
import { FlavorFinderBackButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";
import { useDataPreparation } from "./useDataPreparation";
import "./FlavorFinderConsent.global.scss";
import styles from "./FlavorFinderConsent.module.scss";
const { sanitize } = dompurify;
const defaultValues = {
    email: "",
    consent_purposes: {},
};
export const FlavorFinderConsent = () => {
    const { flavorFinderData, handlePreviousStepClick, setCurrentView } = useFlavorFinderContext();
    const { user, isUserLoggedIn } = useUser();
    const config = getConfig();
    const form = useForm({ defaultValues, shouldUnregister: false, mode: "onChange" });
    const { prepareOmpPayload, prepareSnpPayload } = useDataPreparation();
    const [isPurposesLoading, setIsPurposesLoading] = useState(true);
    const [isSubscribedInProgress, setIsSubscribedInProgress] = useState(false);
    const [consents, setConsents] = useState([]);
    const [isUserConsentAlready, setIsUserConsentAlready] = useState(false);
    const { reset, formState, getValues } = form;
    const snp = useMemo(() => SnpApi.init(), []);
    const cas = useMemo(() => CasPurposesApi.init(), []);
    const isButtonDisabled = useMemo(() => !formState.isDirty || !formState.isValid, [formState.isDirty, formState.isValid]);
    const leaveStep = () => {
        reset();
        setCurrentView("results");
    };
    const subscribeToServices = async (sendSnp = true) => {
        try {
            await OmpApi.init().sendMessage(prepareOmpPayload(getValues()), "DEFAULT");
            sendSnp && (await snp.subscribe(prepareSnpPayload(getValues())));
        }
        catch (error) {
            console.error(error);
        }
        finally {
            leaveStep();
        }
    };
    const onSubscriptionSubmitHandle = async () => {
        await subscribeToServices();
    };
    const onNextStepHandle = async () => {
        if (isUserLoggedIn) {
            try {
                setIsSubscribedInProgress(true);
                await subscribeToServices(false);
            }
            catch (error) {
                console.error(error);
            }
            finally {
                setIsSubscribedInProgress(false);
                leaveStep();
            }
        }
        leaveStep();
    };
    const fetchPurposes = useCallback(async (isLoggedIn) => {
        return isLoggedIn === true ? cas.getPurposesForUserByStep("quiz", false) : cas.getPurposesByStep("quiz");
    }, []);
    const prepareFormData = useCallback((response, isLoggedIn, email) => {
        const baseData = { email: email || "" };
        if (isLoggedIn === true) {
            const purposes = response.reduce((acc, item) => ({ ...acc, [item.id]: item.accepted || false }), {});
            return { ...baseData, consent_purposes: purposes };
        }
        return baseData;
    }, []);
    useEffect(() => {
        if (consents.length) {
            setIsPurposesLoading(false);
            return;
        }
        setIsPurposesLoading(true);
        fetchPurposes(isUserLoggedIn)
            .then((response) => {
            setConsents(response);
            if (isUserLoggedIn === true) {
                setIsUserConsentAlready(response.every((consent) => consent.accepted));
            }
            reset(prepareFormData(response, isUserLoggedIn, user?.email));
        })
            .catch((error) => {
            console.error(error);
        })
            .finally(() => {
            setIsPurposesLoading(false);
        });
    }, [consents.length, reset, user?.email, fetchPurposes, isUserLoggedIn, prepareFormData]);
    const { title, indicatorOverrideLabel, descriptionLoggedIn, descriptionNotLoggedIn, emailFieldLabel, emailFieldValidationErrorMessage, sendResultsCTALabel, emailFieldPlaceholder, backgroundImage, mediumBackgroundImage, smallBackgroundImage, disclaimerLoggedInConsent = "", disclaimerLoggedInNoConsent = "", disclaimerNotLoggedIn = "", } = flavorFinderData.consentPolicy;
    const description = isUserLoggedIn ? descriptionLoggedIn : descriptionNotLoggedIn;
    const isFormShown = !isUserLoggedIn || !isUserConsentAlready;
    const isRecaptchaShown = config.reCaptchaEnabled && config.brandingInformationEnabled;
    const resultsLabel = isUserLoggedIn && isUserConsentAlready
        ? flavorFinderData.showResultsLabel
        : flavorFinderData.skipAndShowResultsCtaLabel;
    const getDisclaimerText = () => {
        if (!isUserLoggedIn) {
            return disclaimerNotLoggedIn;
        }
        return isUserConsentAlready ? disclaimerLoggedInConsent : disclaimerLoggedInNoConsent;
    };
    const disclaimerClasses = classNames(styles.disclaimer, {
        [styles.disclaimerStandalone]: !isFormShown || !isRecaptchaShown,
    });
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.consentBackButtonSmallScreen, children: _jsx(FlavorFinderBackButton, { onClick: handlePreviousStepClick, label: flavorFinderData.backCtaLabel }) }), _jsx("div", { className: styles.consentWrapper, children: _jsxs("div", { className: styles.consent, "data-testid": "flavorFinderConsent", children: [_jsx(FlavorFinderHeader, { title: title, infoText: indicatorOverrideLabel, children: _jsx("div", { className: styles.consentBackButtonBigScreen, children: _jsx(FlavorFinderBackButton, { onClick: handlePreviousStepClick, label: flavorFinderData.backCtaLabel }) }) }), _jsxs("div", { className: styles.content, children: [isPurposesLoading && (_jsx("div", { className: styles.consentLoading, "data-testid": "flavorFinderConsentLoading", children: _jsx(Loading, {}) })), !isPurposesLoading && (_jsxs(_Fragment, { children: [_jsx(ResponsiveImage, { largeSrc: backgroundImage ?? "", mediumSrc: mediumBackgroundImage, smallSrc: smallBackgroundImage }), _jsxs("div", { className: `${styles.column} ${styles.columnBgColor}`, children: [description && (_jsx("p", { className: styles.description, "data-testid": "flavorFinderConsentDescription", dangerouslySetInnerHTML: {
                                                        __html: sanitize(description),
                                                    } })), isFormShown && (_jsxs(Form, { form: form, onSubmit: onSubscriptionSubmitHandle, dataTestId: "flavorFinderConsentForm", children: [_jsxs("div", { className: styles.emailWrapper, children: [_jsx("span", { className: styles.emailIcon, children: _jsx(Mail, {}) }), _jsx(FormField, { name: "email", dataTestId: "flavorFinderFormEmail", label: emailFieldLabel, validation: {
                                                                        required: emailFieldValidationErrorMessage,
                                                                        pattern: validationPatterns.EMAIL_PATTERN,
                                                                    }, render: ({ field }) => (_jsx(DEPRECATED_TextInput, { ...field, type: "email", className: styles.emailInput, placeholder: emailFieldPlaceholder, disabled: isUserLoggedIn, dataTestId: "flavorFinderConsentFormEmailInput" })) })] }), consents.map((consent) => (_jsx("div", { className: styles.consentFormWrapper, children: _jsx(FormField, { className: styles.consentField, renderLabel: () => (_jsx("label", { htmlFor: `consent_purposes.${consent.id}`, className: styles.consentLabel, children: _jsx("p", { className: styles.consentDescription, dangerouslySetInnerHTML: {
                                                                            __html: sanitize(replacePlaceholdersWithLinks(`${consent.description}${consent.mandatory ? "*" : ""}`, config.ONE_TRUST_DYNAMIC_LINKS ?? [])),
                                                                        } }) })), name: `consent_purposes.${consent.id}`, defaultValue: consent.accepted || false, validation: { required: consent.mandatory }, dataTestId: "flavorFinderFormConsentCheckboxes", render: ({ field }) => (_jsx(CheckboxInput, { ...field, id: `consent_purposes.${consent.id}`, className: styles.consentCheckbox, dataTestId: `flavorFinderConsentFormConsentCheckbox.${consent.id}` })) }) }, consent.id))), _jsx(Button, { type: "submit", className: styles.submitButton, color: "primary", theme: "light", isPending: formState.isSubmitting, disabled: isButtonDisabled, "data-testid": "flavorFinderConsentFormSendButton", children: sendResultsCTALabel }), isRecaptchaShown && _jsx(Recaptchabar, { className: "light-theme" })] }, "consentForm")), getDisclaimerText() && (_jsx("div", { className: disclaimerClasses, dangerouslySetInnerHTML: {
                                                        __html: sanitize(getDisclaimerText()),
                                                    } }))] })] }))] }), !isPurposesLoading && (_jsx("div", { className: "flavorfinder__footer", children: _jsx(Button, { variant: "cta", color: "quaternary", theme: "light", className: classNames(styles.consentNextButton, {
                                    [styles.consentNextButtonPending]: isSubscribedInProgress,
                                }), onClick: onNextStepHandle, disabled: isSubscribedInProgress, children: resultsLabel }) }))] }) })] }));
};
//# sourceMappingURL=FlavorFinderConsent.js.map